import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLenis } from "lenis/react";
import { motion } from "framer-motion";
import axios from "axios";
import { Star, ArrowUpRight, Phone, ShieldCheck, Clock, DollarSign, HeartHandshake, Users, MapPin } from "lucide-react";
import { AddressInput } from "@/components/booking/AddressInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REVIEW_CARDS = [
  { name: "Google", stars: 5, key: "google" },
  { name: "Product Review", stars: 5, score: "5/5" },
];

const TRUST_STRIP = [
  { icon: ShieldCheck, label: "Fully insured movers" },
  { icon: Clock, label: "Scheduled move times" },
  { icon: DollarSign, label: "Clear pricing before you book" },
  { icon: HeartHandshake, label: "Care-focused handling" },
  { icon: Users, label: "Friendly support team" },
];

const Stars = ({ n }) => (
  <span className="flex items-center gap-0.5">
    {[...Array(5)].map((_, i) => (
      <Star key={i} className={`h-3.5 w-3.5 ${i < n ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
    ))}
  </span>
);

export const Hero = ({ suburb = null }) => {
  const navigate = useNavigate();
  const lenis = useLenis();
  const [pickup, setPickup] = useState(suburb || "");
  const [drop, setDrop] = useState("");
  const [googleScore, setGoogleScore] = useState("4.8/5");

  useEffect(() => {
    axios.get(`${API}/reviews`).then(({ data }) => {
      if (data.live && data.rating) setGoogleScore(`${data.rating}/5`);
    }).catch(() => {});
  }, []);

  const goQuote = () => {
    const params = new URLSearchParams();
    if (pickup.trim()) params.set("pickup_suburb", pickup.trim());
    if (drop.trim()) params.set("drop_suburb", drop.trim());
    const base = typeof window !== "undefined" ? window.location.pathname : "/";
    navigate(`${base}?${params.toString()}#quote`, { replace: true });
    lenis?.scrollTo("#quote", { offset: -70 });
  };

  return (
    <section id="top" className="relative flex flex-col overflow-hidden lg:min-h-[100svh]" data-testid="hero-section">
      <div className="absolute inset-0 grid grid-cols-2 lg:grid-cols-3" aria-hidden="true" data-testid="hero-bg-collage">
        <img src="/images/bg-truck-side.jpg" alt="" className="h-full w-full object-cover" fetchPriority="high" />
        <img src="/images/bg-movers-loading.jpg" alt="" className="h-full w-full object-cover" />
        <img src="/images/bg-movers-boxes.jpg" alt="" className="hidden h-full w-full object-cover lg:block" />
      </div>
      <div className="absolute inset-0 bg-white/[0.55] sm:bg-white/[0.45]" />
      <div className="absolute inset-0 bg-gradient-to-b from-white/20 via-transparent to-white/50" />

      <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 items-center px-5 pb-8 pt-20 lg:px-10 lg:pb-16 lg:pt-36">
        <div className="grid w-full items-center gap-6 lg:grid-cols-2 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-start-1 lg:row-start-1"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white/80 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-700 backdrop-blur-sm sm:px-4 sm:py-2 sm:text-[11px]">
              <Star className="h-3 w-3 fill-amber-400 text-amber-400 sm:h-3.5 sm:w-3.5" />
              {suburb ? `Removalists in ${suburb}` : "Australia\u2019s Trusted Moving Team"}
            </span>

            <h1 className="mt-4 font-display text-3xl font-semibold leading-[1.08] tracking-tight text-slate-900 sm:text-6xl lg:mt-6 lg:text-6xl xl:text-7xl" data-testid="hero-heading">
              {suburb ? (
                <>Removalists in {suburb} for <span className="text-blaze">Local &amp; House Moves</span></>
              ) : (
                <>Reliable Removalists in Melbourne for <span className="text-blaze">Local &amp; House Moves</span></>
              )}
            </h1>

            <p className="mt-3 max-w-lg text-xs leading-relaxed text-slate-700 lg:mt-6 lg:text-base xl:text-lg">
              {suburb ? (
                <>Looking for cheap removalists and movers near you in {suburb}? MoversNearby are local {suburb} removalists offering house moves, furniture removals and office relocations from $69/hr. Clear pricing, fully insured crews and careful handling &mdash; book your {suburb} move online or get a free quote.</>
              ) : (
                <>Looking for reliable removalists in Melbourne? MoversNearby makes moving simple with
                professional movers, clear pricing and careful handling of your belongings. Whether
                you&rsquo;re moving across the street, across Melbourne or into a new home, our experienced
                moving team is ready to help.</>
              )}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-2xl border border-slate-200 bg-white p-4 shadow-xl sm:p-6 lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:p-9"
            data-testid="hero-quote-card"
          >
            <h2 className="font-display text-lg font-semibold text-slate-900 sm:text-2xl">
              Get a <span className="text-blaze">FREE</span> Moving Quote
            </h2>

            <div className="mt-4 space-y-3 sm:mt-6 sm:space-y-4">
              <div className="rounded-xl border border-slate-200 p-3.5">
                <label className="mb-1 flex items-center gap-2 text-xs font-bold text-slate-900">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-50">
                    <MapPin className="h-4 w-4 text-blaze" />
                  </span>
                  Pickup From
                </label>
                <AddressInput mode="suburb" placeholder="Enter pickup suburb or postcode" value={pickup} onChange={setPickup} testid="hero-pickup" inputClassName="w-full border-0 bg-transparent pl-11 text-sm text-slate-900 placeholder-slate-400 outline-none" />
              </div>
              <div className="rounded-xl border border-slate-200 p-3.5">
                <label className="mb-1 flex items-center gap-2 text-xs font-bold text-slate-900">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-50">
                    <MapPin className="h-4 w-4 text-blaze" />
                  </span>
                  Deliver To
                </label>
                <AddressInput mode="suburb" placeholder="Enter delivery suburb or postcode" value={drop} onChange={setDrop} testid="hero-drop" inputClassName="w-full border-0 bg-transparent pl-11 text-sm text-slate-900 placeholder-slate-400 outline-none" />
              </div>
              <button
                onClick={goQuote}
                data-testid="hero-quote-btn"
                className="group flex w-full items-center justify-center gap-3 rounded-xl bg-red-600 px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-all duration-300 hover:scale-[1.02] hover:bg-red-700 hover:shadow-[0_12px_40px_rgba(220,38,38,0.35)]"
              >
                Request a Quote
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
              <p className="text-center text-sm text-slate-600">
                Prefer to talk? Call{" "}
                <a href="tel:0391136005" className="font-semibold text-blaze hover:underline" data-testid="hero-call-btn">
                  03 9113 6005
                </a>
              </p>
              <div className="flex items-center justify-center gap-2.5 pt-1">
                <a
                  href="tel:0391136005"
                  data-testid="hero-call-now-btn"
                  className="flex items-center gap-2 rounded-md bg-blaze px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-white transition-all duration-300 hover:scale-[1.03] hover:bg-blue-700"
                >
                  <Phone className="h-3.5 w-3.5" /> Call Now
                </a>
                <button
                  type="button"
                  onClick={goQuote}
                  data-testid="hero-quote-online-link"
                  className="rounded-md border-2 border-blaze px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-blaze transition-all duration-300 hover:scale-[1.03] hover:bg-blaze hover:text-white"
                >
                  Get a Free Quote Online
                </button>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-start-1 lg:row-start-2 lg:self-end"
          >
            <div className="grid max-w-lg grid-cols-3 gap-2 sm:gap-3" data-testid="hero-review-cards">
              {REVIEW_CARDS.map((r) => (
                <div key={r.name} className="rounded-xl border border-slate-200 bg-white/85 px-2 py-2 shadow-sm backdrop-blur-sm sm:px-4 sm:py-3.5" data-testid={`hero-rating-${r.name.toLowerCase().replace(/\s/g, "-")}`}>
                  <p className="text-[11px] font-bold text-slate-900 sm:text-xs">{r.name}</p>
                  <div className="mt-1.5 flex flex-wrap items-center gap-1">
                    <Stars n={r.stars} />
                    <span className="text-[11px] font-semibold text-slate-700 sm:text-xs">{r.key === "google" ? googleScore : r.score}</span>
                  </div>
                  <p className="mt-1 hidden text-[10px] text-slate-500 sm:block">Rated by our valued clients.</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      <div className="relative z-10 bg-slate-900" data-testid="hero-trust-badges">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-10 gap-y-3 px-5 py-5 lg:justify-between lg:px-10">
          {TRUST_STRIP.map((t) => (
            <span key={t.label} className="flex items-center gap-2.5 text-xs font-semibold text-slate-200">
              <t.icon className="h-4 w-4 text-blue-400" strokeWidth={2} />
              {t.label}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};
